import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import type {
  ConnectedFacility,
  ConnectedFacilityEventHistory,
  ConnectedFacilityNote,
  PaginatedConnectedFacilityNoteList,
  PatchedConnectedFacilityEventUpdate,
  PatchedConnectedFacilityNote,
  PatchedConnectedFacilityStatusUpdate,
} from '../../../../../js/api';
import { ConnectionsService } from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { connectionRequestsQueryKeys } from '../../../../../js/app/query-keys-factories/connection-requests';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { Avatar } from '../../../components/avatar';
import { Button } from '../../../components/button';
import { ConnectionRateHistoryTable } from '../../../components/connection-rate-history-table';
import { DocumentsTab } from '../../../components/documents-tab';
import { DrawerCloseButton } from '../../../components/drawer';
import { DropdownSelect } from '../../../components/dropdown-select';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsButton, TabsContent } from '../../../components/tabs';
import { TooltippedText } from '../../../components/tooltipped-text';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { EventCodes, StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type {
  ActivityEventHistory,
  ActivityHistory,
  ActivityStatusHistory,
} from '../../../utils/activity-types';
import { getStatusTextStyle } from '../../../utils/statusStyles';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';
import { CLEARANCE_QUERY_KEY, ORGANIZATION_CLEARANCE_LIST_QUERY_KEY } from '../Clearance/queryKeys';
import { AssignmentsTab } from './AssignmentsTab';
import { ConnectionQuickDetails } from './ConnectionQuickDetails';
import { DetailsTab } from './DetailsTab';
import { OpportunityTab } from './OpportunityTab';
import { PresentationInfoTab } from './PresentationInfoTab';
import {
  CONNECTION_CHANGE_HISTORY_QUERY_KEY,
  CONNECTION_EVENT_HISTORY_QUERY_KEY,
  CONNECTION_NOTES_QUERY_KEY,
  CONNECTION_QUERY_KEY,
  CONNECTION_STATUS_HISTORY_QUERY_KEY,
  ORGANIZATION_CONNECTIONS_LIST_QUERY_KEY,
} from './queryKeys';

const WORKFLOWS_META_QUERY_KEY = 'connections.connectedfacility';

type ConnectionDrawerProps =
  | {
      connection: ConnectedFacility;
      connectionId?: never;
      onClose?: () => void;
      prevId?: string | null;
      nextId?: string | null;
      selectedQueueCode?: string | undefined;
    }
  | {
      connection: never;
      connectionId: string;
      onClose?: () => void;
      prevId?: string | null;
      nextId?: string | null;
      selectedQueueCode?: string | undefined;
    };

// eslint-disable-next-line sonarjs/cognitive-complexity
export function ConnectionDrawer({
  connection,
  connectionId,
  onClose,
  prevId,
  nextId,
  selectedQueueCode,
}: ConnectionDrawerProps) {
  const queryClient = useQueryClient();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [pendingTargetStatus, setPendingTargetStatus] = useState<string | null>(null);
  const { user } = useContext(SessionContext);
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch connection if only ID is provided
  const {
    data: fetchedConnection,
    isLoading: isLoadingConnection,
    error: connectionError,
  } = useQuery<ConnectedFacility>({
    queryKey: [CONNECTION_QUERY_KEY, connectionId],
    queryFn: () => ConnectionsService.connectionsConnectedFacilityRetrieve({ id: connectionId! }),
    enabled: !!connectionId && !connection,
  });

  // Fetch workflow metadata (standardized via hook)
  const { statuses: workflowStatuses } = useWorkflowMetadata(WORKFLOWS_META_QUERY_KEY);

  // Use provided connection or fetched connection
  const connectionData = connection || fetchedConnection;
  const effectiveConnectionId = connectionId || connectionData?.id;

  // Helper: after an action refreshes, if this item moved out of the current queue, auto-advance
  const checkAndNavigateIfRemoved = (updated?: ConnectedFacility) => {
    // Only auto-advance when a specific queue (not 'all') is selected
    if (!selectedQueueCode || selectedQueueCode === 'all') return;

    const currentStatusCode =
      updated?.current_status?.code ?? connectionData?.current_status?.code ?? null;
    const queueName = getQueueNameForStatus(workflowStatuses, currentStatusCode);

    if (queueName && queueName !== selectedQueueCode) {
      const targetId = nextId || prevId || null;
      if (targetId) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('id', targetId);
        setSearchParams(nextParams, { replace: true });
      } else {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('id');
        setSearchParams(nextParams, { replace: true });
        onClose?.();
      }
    }
  };

  // Fetch notes
  const { data: notesData } = useQuery({
    queryKey: [CONNECTION_NOTES_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityNotesList({
        id: effectiveConnectionId!,
        ordering: '-created_at',
      }),
    enabled: !!effectiveConnectionId,
    select: (
      data: PaginatedConnectedFacilityNoteList | ConnectedFacilityNote | ConnectedFacilityNote[]
    ) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      if ('results' in data) {
        return data.results || [];
      }
      return [data as ConnectedFacilityNote];
    },
  });

  // Fetch status history
  const { data: statusHistory } = useQuery({
    queryKey: [CONNECTION_STATUS_HISTORY_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityStatusHistoryRetrieve({
        id: effectiveConnectionId!,
        sort: 'desc',
      }),
    enabled: !!effectiveConnectionId,
  });

  // Fetch change history
  const { data: changeHistory } = useQuery({
    queryKey: [CONNECTION_CHANGE_HISTORY_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityChangeHistoryRetrieve({
        id: effectiveConnectionId!,
        sort: 'desc',
      }),
    enabled: !!effectiveConnectionId,
  });

  // Fetch event history
  const { data: eventHistoryData } = useQuery({
    queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityEventHistoryRetrieve({
        id: effectiveConnectionId!,
        sort: 'desc',
      }),
    enabled: !!effectiveConnectionId,
    select: (data: ConnectedFacilityEventHistory | ConnectedFacilityEventHistory[]) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [data];
    },
  });

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      statusCode: string | null;
      reasonCode?: string | null;
      comment?: string;
    }) => {
      const statusUpdateData: PatchedConnectedFacilityStatusUpdate = {
        code: data.statusCode || undefined,
        reason_code: data.reasonCode ?? null,
        comment: data.comment || null,
      };
      return ConnectionsService.connectionsConnectedFacilityStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: async (updatedConnection, variables) => {
      const { id } = variables;
      toast.success('Connection status successfully updated');
      queryClient.setQueryData([CONNECTION_QUERY_KEY, id], updatedConnection);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CONNECTION_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CONNECTIONS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CLEARANCE_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_CHANGE_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_NOTES_QUERY_KEY, id] }),

        // Invalidate queue notification badges for this scope
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.ConnectedFacility],
        }),
        // Invalidate global menu badges
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);

      // If item moved out of current queue, navigate to next/prev or close
      checkAndNavigateIfRemoved(updatedConnection as ConnectedFacility);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update connection status' });
    },
  });

  // Event update mutation (for event buttons)
  const eventUpdateMutation = useMutation({
    mutationFn: async (data: { id: string; eventCode: string }) => {
      const eventUpdateData: PatchedConnectedFacilityEventUpdate = {
        code: data.eventCode,
      };
      return ConnectionsService.connectionsConnectedFacilityEventUpdatePartialUpdate({
        id: data.id,
        requestBody: eventUpdateData,
      });
    },
    onSuccess: async (_updatedConnection, variables) => {
      const { id } = variables;
      toast.success('Event logged successfully');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CONNECTION_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CONNECTIONS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CLEARANCE_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_CHANGE_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_NOTES_QUERY_KEY, id] }),
        // Invalidate queue notification badges for this scope
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.ConnectedFacility],
        }),
        // Invalidate global menu badges
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);

      // Fetch the latest connection and check if it should auto-advance
      try {
        const latest = await ConnectionsService.connectionsConnectedFacilityRetrieve({ id });
        checkAndNavigateIfRemoved(latest as ConnectedFacility);
      } catch (_e) {
        // swallow; navigation not critical here
      }
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to approve connection' });
    },
  });

  // Handle status modal confirm
  const handleStatusModalConfirm = async (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
  }) => {
    if (!effectiveConnectionId) {
      toast.error('Connection ID not found');
      return;
    }

    await statusUpdateMutation.mutateAsync({
      id: effectiveConnectionId,
      statusCode: data.statusCode,
      reasonCode: data.reasonCode,
      comment: data.comment,
    });
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  // Handle EVENT button click
  const handleEventClick = async (eventCode: EventCodes) => {
    if (!effectiveConnectionId) {
      toast.error('Connection ID not found');
      return;
    }

    await eventUpdateMutation.mutateAsync({
      id: effectiveConnectionId,
      eventCode: eventCode,
    });
  };

  // Relevant status checks
  const isPendingFacRev = connectionData?.current_status?.code === StatusCodes.PEND_FAC_REV;
  const isPendConfirm = connectionData?.current_status?.code === StatusCodes.PEND_CONFIRM;
  const isOnHold = connectionData?.current_status?.code === StatusCodes.ON_HOLD;
  const isPaused = connectionData?.current_status?.code === StatusCodes.PAUSED;
  const isAwaitPriv = connectionData?.current_status?.code === StatusCodes.AWAIT_PRVLG;
  const isProvActionReq = connectionData?.current_status?.code === StatusCodes.PROV_ACTION_REQ;
  const isHCOStatus =
    workflowStatuses?.[connectionData?.current_status.code]?.set_by_healthcare_entity;

  // Check if the status is not final and the user holds permissions (for edit button)
  const isEditable =
    user?.permissions.includes('update__connections__connectedfacility') && isHCOStatus;

  // Build tabs list and manage active tab state (to support dropdown rendering on small/overflow)
  const baseTabs = useMemo(
    () => [
      { code: 'details', label: 'Details' as const },
      { code: 'opportunity', label: 'Opportunity' as const },
      { code: 'assignments', label: 'Assignments' as const },
      { code: 'rate-history', label: 'Rate History' as const },
      { code: 'documents', label: 'Documents' as const },
    ],
    []
  );
  const tabs = useMemo(
    () =>
      isPendingFacRev
        ? [{ code: 'presentation-info', label: 'Presentation Info' as const }, ...baseTabs]
        : baseTabs,
    [isPendingFacRev, baseTabs]
  );

  const initialTab = isPendingFacRev ? 'presentation-info' : 'details';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const lastAppliedConnectionId = useRef<string | null>(null);

  // Ensure activeTab is valid and apply default when connection data becomes available.
  // This fixes the case where we initialized with wrong value during loading (connectionData
  // was undefined, so initialTab was always 'details').
  useEffect(() => {
    const connectionId = connectionData?.id;
    if (!connectionId) return;

    const codes = new Set(tabs.map((t) => t.code));

    // If current tab is invalid, fix it
    if (!codes.has(activeTab)) {
      setActiveTab(tabs[0]?.code ?? initialTab);
      lastAppliedConnectionId.current = connectionId;
      return;
    }

    // When connection data first becomes available (or we switch connections), apply default tab
    if (lastAppliedConnectionId.current !== connectionId) {
      lastAppliedConnectionId.current = connectionId;
      setActiveTab(initialTab);
    }
  }, [connectionData?.id, isPendingFacRev, tabs, activeTab, initialTab]);

  // Detect overflow of the tabs list to auto-collapse into a dropdown
  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
    });

  if (isLoadingConnection) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8" data-testid="connection-drawer">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading connection details...</div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8" data-testid="connection-drawer">
        <div className="tw:text-stoplight-danger">
          Error loading connection details. Please try again.
        </div>
      </div>
    );
  }

  if (!connectionData) {
    return null;
  }

  return (
    <>
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        initialStatusCode={connectionData?.current_status?.code || null}
        targetStatusCode={pendingTargetStatus}
        workflowScope="connections.connectedfacility"
        isPending={statusUpdateMutation.isPending}
        currentReasonCode={connectionData?.current_reason?.code || null}
        currentStatusTitle={connectionData?.current_status?.title || null}
      />
      <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-6" data-testid="connection-drawer">
        {/* Top Section - Provider Info, Status, Close */}
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:mb-4">
          {/* Provider Avatar - hidden on mobile */}
          {connectionData.provider?.profile_picture ? (
            <Avatar
              src={connectionData.provider.profile_picture}
              alt={connectionData.provider?.display_name}
              className="tw:size-12 tw:shrink-0 tw:md:order-1"
            />
          ) : (
            <div className="tw:flex tw:items-center tw:justify-center tw:size-12 tw:shrink-0 tw:bg-brand-green-1 tw:text-white tw:rounded-full tw:md:order-1">
              <i className="fa-solid fa-user tw:text-lg" />
            </div>
          )}
          {/* Provider Name and View Profile link */}
          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:flex-1 tw:min-w-0 tw:order-2">
            <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
              <span
                className="tw:text-base tw:md:text-xl tw:font-bold"
                data-testid="connection-drawer-provider-name"
              >
                {connectionData.provider?.display_name}
              </span>
              {connectionData.provider?.display_name && !isMobile && (
                <TooltippedText
                  placement="bottom"
                  tooltipContent={`View All connections for this provider`}
                >
                  <Button
                    color="light"
                    small
                    className="tw:shrink-0 tw:!text-brand-green-0 tw:!rounded-lg"
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      next.set('search', connectionData.provider!.display_name);
                      next.set('queue', 'all');
                      setSearchParams(next, { replace: true });
                    }}
                    aria-label={`Filter list by ${connectionData.provider.display_name}`}
                  >
                    <i className="fas fa-filter tw:py-1" />
                  </Button>
                </TooltippedText>
              )}
            </div>
            {connectionData.provider?.id && (
              <Link
                to={`/app/healthcare-entity/providers/${connectionData.provider.id}`}
                className="tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0 tw:w-fit"
              >
                View Profile
              </Link>
            )}
          </div>
          {/* Status Pill and reason - always below provider info (left-aligned) */}
          <div className="tw:order-4 tw:w-full tw:flex tw:flex-col tw:items-start tw:gap-2">
            <StatusPill
              scope={WorkflowScopes.ConnectedFacility}
              status={connectionData.current_status}
            />
            {connectionData.current_reason && (
              <div className="tw:flex tw:items-center tw:gap-2">
                <div
                  className={twMerge(
                    'tw:text-xs',
                    getStatusTextStyle(connectionData.current_status)
                  )}
                >
                  {connectionData.current_reason.title}
                </div>
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => setIsStatusModalOpen(true)}
                    className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                    aria-label="Edit reason"
                  >
                    <i className="fas fa-edit tw:text-xs" />
                  </button>
                )}
              </div>
            )}
          </div>
          {/* Close Button - right side of row 1 */}
          {onClose && (
            <DrawerCloseButton
              onClick={onClose}
              className="tw:order-3"
              data-testid="connection-drawer-close"
            />
          )}
        </div>

        {/* Facility Details Section */}
        <ConnectionQuickDetails connection={connectionData} />

        {/* Status Transition Buttons */}
        <>
          {user?.permissions?.includes('update__connections__connectedfacility') && (
            <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2 tw:mt-4">
              {isPendingFacRev && (
                <Button
                  color="brandgreen0"
                  onClick={() => handleEventClick(EventCodes.FACILITY_APPROVED)}
                  disabled={eventUpdateMutation.isPending}
                  small={isMobile}
                >
                  {eventUpdateMutation.isPending ? 'Approving...' : 'Approve'}
                </Button>
              )}
              {isProvActionReq && (
                <Button
                  color="brandgreen0"
                  onClick={() => handleEventClick(EventCodes.PROV_INFO_RECVD)}
                  disabled={eventUpdateMutation.isPending}
                  small={isMobile}
                >
                  {eventUpdateMutation.isPending
                    ? 'Provider Info Received...'
                    : 'Provider Info Received'}
                </Button>
              )}
              {isPendConfirm && (
                <Button
                  color="brandgreen0"
                  onClick={() => handleEventClick(EventCodes.CONF_LTR_SIGNED)}
                  disabled={eventUpdateMutation.isPending}
                  small={isMobile}
                >
                  {eventUpdateMutation.isPending
                    ? 'Marking Letter Signed...'
                    : 'Confirmation Letter Signed'}
                </Button>
              )}
              {isAwaitPriv && (
                <Button
                  color="brandgreen0"
                  onClick={() => handleEventClick(EventCodes.PRIVILEGES_GRANTED)}
                  disabled={eventUpdateMutation.isPending}
                  small={isMobile}
                >
                  {eventUpdateMutation.isPending ? 'Privileges Granted...' : 'Privileges Granted'}
                </Button>
              )}
              {isOnHold && (
                <Button
                  color="brandgreen0"
                  onClick={() => handleEventClick(EventCodes.REMOVED_HOLD)}
                  disabled={eventUpdateMutation.isPending}
                  small={isMobile}
                >
                  {eventUpdateMutation.isPending ? 'Removing Hold...' : 'Remove Hold'}
                </Button>
              )}
              {isPaused && (
                <Button
                  color="brandgreen0"
                  onClick={() => handleEventClick(EventCodes.REACTIVATED)}
                  disabled={eventUpdateMutation.isPending}
                  small={isMobile}
                >
                  {eventUpdateMutation.isPending ? 'Reactivating..' : 'Reactivate'}
                </Button>
              )}
              <StatusTransitionButtons
                isLoading={isLoadingConnection}
                currentStatusCode={connectionData?.current_status?.code}
                workflowMetadata={workflowStatuses as StatusMetadata}
                onStatusClick={(status) => {
                  setPendingTargetStatus(status.code);
                  setIsStatusModalOpen(true);
                }}
                isPending={statusUpdateMutation.isPending}
                small={isMobile}
              />
            </div>
          )}
        </>
        {/* Tabs Section */}
        <Tabs activeTab={activeTab} onChange={setActiveTab} className="tw:mt-4">
          {isOverflowCollapsed ? (
            <div className="tw:pb-2" data-testid="connection-drawer-section-dropdown">
              <div className="tw:text-xs tw:text-zinc-500 tw:mb-1 tw:pl-1">Select View</div>
              <DropdownSelect
                inputId="connection-drawer-section"
                aria-label="Connection section"
                allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
                value={activeTab}
                isSearchable={false}
                isClearable={false}
                onChange={(opt: any) => {
                  if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                }}
              />
            </div>
          ) : (
            <div className="tw:w-full">
              <div className="tw:border-b tw:border-zinc-200 tw:dark:border-zinc-700">
                <nav
                  ref={tabsMeasureRef as React.RefObject<HTMLElement>}
                  className="tw:-mb-px tw:flex tw:space-x-4"
                  aria-label="Tabs"
                >
                  {tabs.map((t) => (
                    <TabsButton key={t.code} tab={t.code} dataTestId={`drawer-tab-${t.code}`}>
                      {t.label}
                    </TabsButton>
                  ))}
                </nav>
              </div>
            </div>
          )}

          {isPendingFacRev && (
            <TabsContent tab="presentation-info">
              <PresentationInfoTab connection={connectionData} />
            </TabsContent>
          )}

          <TabsContent tab="details">
            <DetailsTab connection={connectionData} />
          </TabsContent>

          <TabsContent tab="opportunity">
            <OpportunityTab connection={connectionData} />
          </TabsContent>

          <TabsContent tab="assignments">
            <AssignmentsTab connection={connectionData} />
          </TabsContent>

          <TabsContent tab="rate-history">
            <ConnectionRateHistoryTable rateHistory={connectionData.rate_history || []} />
          </TabsContent>

          <TabsContent tab="documents">
            <DocumentsTab
              contextType="connections.connectedfacility"
              contextId={connectionData.id}
              ownerId={connectionData.healthcare_entity?.id ?? ''}
              relatedProviderId={connectionData.provider?.id}
            />
          </TabsContent>
        </Tabs>

        <div className="tw:mt-2 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
          <SectionHeader className="tw:mb-2  tw:p-1 tw:text-brand-green-0" headerText="Activity" />
          <ActivityLog
            enableCommentsFilter={true}
            enableStatusChangesFilter={true}
            enableUpdatesFilter={true}
            enableEventsFilter={true}
            composerPlaceholder="Enter a comment (visible to all parties)..."
            notes={notesData}
            statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
            changeHistory={changeHistory as ActivityHistory[] | undefined}
            eventHistory={eventHistoryData as ActivityEventHistory[] | undefined}
            noteMutations={{
              createNote: async (data) => {
                return ConnectionsService.connectionsConnectedFacilityNotesCreate({
                  id: effectiveConnectionId!,
                  requestBody: {
                    connected_facility: effectiveConnectionId!,
                    comment: data.comment,
                  } as ConnectedFacilityNote,
                });
              },
              updateNote: async (noteId: string, data: PatchedConnectedFacilityNote) => {
                return ConnectionsService.connectionsConnectedFacilityNotePartialUpdate({
                  id: noteId,
                  requestBody: data,
                });
              },
              deleteNote: async (noteId: string) => {
                return ConnectionsService.connectionsConnectedFacilityNoteDestroy({
                  id: noteId,
                });
              },
              notesQueryKey: [CONNECTION_NOTES_QUERY_KEY, effectiveConnectionId],
              extraInvalidateQueryKeys: [
                [CONNECTION_STATUS_HISTORY_QUERY_KEY, effectiveConnectionId],
              ],
            }}
          />
        </div>
      </div>
    </>
  );
}
